import React, { Fragment, lazy } from "react";
import { Navigate, Route } from "react-router-dom";

import { AuthActionsView } from "@app/pages/auth/AuthActionsView";
import { AuthWrapper } from "@domain/routing/AuthWrapper";
import { HomeView } from "@app/pages/HomeView";
import { VerifyEmailView } from "@app/pages/auth/VerifyEmailView";
import { AppRoutes, getRoute, ModuleSubGroups } from "@domain/routing/routeConstants";
import { GQL } from "@binale-tech/shared";
import { LoginView } from "@app/pages/auth/LoginView/LoginView";
import { NewPasswordView } from "@app/pages/auth/NewPasswordView";
import { NotFoundView } from "@app/pages/static/ErrorViews";
import { PasswordResetView } from "@app/pages/auth/PasswordResetView";
import { ProductKeys } from "@domain/models/Product";
import { RegisterView } from "@app/pages/auth/RegisterView";

// Every view below is code-split: its chunk is fetched on first navigation
// instead of shipping in the startup bundle. The Suspense boundary rendering
// the loading skeleton lives in AppLayout (App.tsx). Only the first-paint
// pages (home, auth) and tiny error views stay eager above.
const AGBView = lazy(() => import("@app/pages/static/AGBView").then((m) => ({ default: m.AGBView })));
const ERAnzView = lazy(() => import("@accounting/er/erfassung/ERAnzView").then((m) => ({ default: m.ERAnzView })));
const AuswertungAvisViewER = lazy(() =>
    import("@accounting/er/auswertung/AuswertungAvisView").then((m) => ({ default: m.AuswertungAvisViewER })),
);
const AuswertungAvisViewErA = lazy(() =>
    import("@accounting/er/auswertung/AuswertungAvisView").then((m) => ({ default: m.AuswertungAvisViewErA })),
);
const AuswertungFalligkeitER = lazy(() =>
    import("@accounting/er/auswertung/AuswertungFalligkeitView").then((m) => ({ default: m.AuswertungFalligkeitER })),
);
const AuswertungFalligkeitViewErA = lazy(() =>
    import("@accounting/er/auswertung/AuswertungFalligkeitView").then((m) => ({
        default: m.AuswertungFalligkeitViewErA,
    })),
);
const AuswertungOposViewER = lazy(() =>
    import("@accounting/er/auswertung/AuswertungOposView").then((m) => ({ default: m.AuswertungOposViewER })),
);
const AuswertungOposViewErA = lazy(() =>
    import("@accounting/er/auswertung/AuswertungOposView").then((m) => ({ default: m.AuswertungOposViewErA })),
);
const BankView = lazy(() => import("@accounting/bank/BankView").then((m) => ({ default: m.BankView })));
const CalendarPageView = lazy(() =>
    import("@accounting/common/calendar/CalendarPageView").then((m) => ({ default: m.CalendarPageView })),
);
const BanksManageView = lazy(() =>
    import("@accounting/bank/BanksManageView").then((m) => ({ default: m.BanksManageView })),
);
const CommonAuswertungView = lazy(() =>
    import("@accounting/common/overviews/CommonAuswertungView").then((m) => ({ default: m.CommonAuswertungView })),
);
const CompaniesView = lazy(() =>
    import("@app/pages/companies/CompaniesView").then((m) => ({ default: m.CompaniesView })),
);
const DatenschutzView = lazy(() =>
    import("@app/pages/static/DatenschutzView").then((m) => ({ default: m.DatenschutzView })),
);
const DebView = lazy(() => import("@accounting/deb/DebView").then((m) => ({ default: m.DebView })));
const FestschreibungAZView = lazy(() =>
    import("@accounting/er/FestschreibungView").then((m) => ({ default: m.FestschreibungAZView })),
);
const FestschreibungERView = lazy(() =>
    import("@accounting/er/FestschreibungView").then((m) => ({ default: m.FestschreibungERView })),
);
const FEView = lazy(() => import("@accounting/fe/FEView").then((m) => ({ default: m.FEView })));
const HaftungsausschlussView = lazy(() =>
    import("@app/pages/static/HaftungsausschlussView").then((m) => ({ default: m.HaftungsausschlussView })),
);
const ImpressumView = lazy(() => import("@app/pages/static/ImpressumView").then((m) => ({ default: m.ImpressumView })));
const CookiesView = lazy(() => import("@app/pages/static/CookiesView").then((m) => ({ default: m.CookiesView })));
const AVVView = lazy(() => import("@app/pages/static/AVVView").then((m) => ({ default: m.AVVView })));
const KBManageView = lazy(() => import("@accounting/kb/KBManageView").then((m) => ({ default: m.KBManageView })));
const KBView = lazy(() => import("@accounting/kb/KBView").then((m) => ({ default: m.KBView })));
const KontoAuskunftView = lazy(() =>
    import("@accounting/common/overviews/KontenAuskunft/KontoAuskunftView").then((m) => ({
        default: m.KontoAuskunftView,
    })),
);
const LAView = lazy(() => import("@accounting/la/LAView").then((m) => ({ default: m.LAView })));

const EditCategoryView = lazy(() =>
    import("@accounting/common/items/EditCategoryView/EditCategoryView").then((m) => ({ default: m.EditCategoryView })),
);
const EditCreditorView = lazy(() =>
    import("@accounting/common/items/EditCreditorView/EditCreditorView").then((m) => ({ default: m.EditCreditorView })),
);
const EditDebView = lazy(() =>
    import("@accounting/common/items/EditDebView/EditDebView").then((m) => ({ default: m.EditDebView })),
);
const EditTagView = lazy(() =>
    import("@accounting/common/items/EditTagView/EditTagView").then((m) => ({ default: m.EditTagView })),
);
const ContactsListView = lazy(() => import("@contacts/index").then((m) => ({ default: m.ContactsListView })));

const DatevExportView = lazy(() =>
    import("@accounting/common/importExport/DatevExportView").then((m) => ({ default: m.DatevExportView })),
);
const DatevImportRecordsView = lazy(() =>
    import("@accounting/common/importExport/DatevImportRecordsView").then((m) => ({
        default: m.DatevImportRecordsView,
    })),
);
const DatevImportAccountsView = lazy(() =>
    import("@accounting/common/importExport/DatevImportAccountsView").then((m) => ({
        default: m.DatevImportAccountsView,
    })),
);
const DMS = lazy(() => import("@dms/index").then((m) => ({ default: m.DMS })));
const AuswertungAvisViewDeb = lazy(() =>
    import("@accounting/deb/auswertung/AuswertungAvisViewDeb").then((m) => ({ default: m.AuswertungAvisViewDeb })),
);
const AuswertungOposViewDeb = lazy(() =>
    import("@accounting/deb/auswertung/AuswertungOposViewDeb").then((m) => ({ default: m.AuswertungOposViewDeb })),
);
const KbBankHomePage = lazy(() =>
    import("@app/components/company/CompanyDetailRequest").then((m) => ({ default: m.KbBankHomePage })),
);
const POSView = lazy(() => import("@accounting/pos/POSView").then((m) => ({ default: m.POSView })));
const ProfileView = lazy(() => import("@app/pages/profile/ProfileView").then((m) => ({ default: m.ProfileView })));
const ReleaseNotesView = lazy(() =>
    import("@app/pages/releaseNotes/ReleaseNotesView").then((m) => ({ default: m.ReleaseNotesView })),
);
const ERView = lazy(() => import("@accounting/er/erfassung/ERView").then((m) => ({ default: m.ERView })));
const SusaView = lazy(() =>
    import("@accounting/common/overviews/SuSa/SusaView").then((m) => ({ default: m.SusaView })),
);
const TestView = lazy(() => import("@app/pages/TestView").then((m) => ({ default: m.TestView })));
const VatView = lazy(() => import("@accounting/common/overviews/UStVA/VatView").then((m) => ({ default: m.VatView })));
const OAuthCallback = lazy(() =>
    import("@accounting/shared/Datev/OAuthCallback").then((m) => ({ default: m.OAuthCallback })),
);
const M365OAuthCallback = lazy(() =>
    import("@dms/features/mailbox/M365OAuthCallback").then((m) => ({ default: m.M365OAuthCallback })),
);
const BWAView = lazy(() => import("@accounting/common/overviews/BWA/BWAView").then((m) => ({ default: m.BWAView })));
const Banks = lazy(() => import("@banks/BanksApp").then((m) => ({ default: m.BanksApp })));
const CashRegisters = lazy(() =>
    import("@cash-register/CashRegisterApp").then((m) => ({ default: m.CashRegisterApp })),
);
const Avm = lazy(() => import("@accounting/avm/AvmApp").then((m) => ({ default: m.AvmApp })));
const Invoices = lazy(() => import("@invoicing/InvoicesApp").then((m) => ({ default: m.InvoicesApp })));
const ERTemplatesView = lazy(() =>
    import("@accounting/er/templates/ERTemplatesView").then((m) => ({ default: m.ERTemplatesView })),
);
const KBTemplatesView = lazy(() =>
    import("@accounting/kb/templates/KBTemplatesView").then((m) => ({ default: m.KBTemplatesView })),
);
const BankTemplatesView = lazy(() =>
    import("@accounting/bank/templates/BankTemplatesView").then((m) => ({ default: m.BankTemplatesView })),
);
const FETemplatesView = lazy(() =>
    import("@accounting/fe/templates/FETemplatesView").then((m) => ({ default: m.FETemplatesView })),
);
const POSTemplatesView = lazy(() =>
    import("@accounting/pos/templates/POSTemplatesView").then((m) => ({ default: m.POSTemplatesView })),
);
const DebTemplatesView = lazy(() =>
    import("@accounting/deb/templates/DebTemplatesView").then((m) => ({ default: m.DebTemplatesView })),
);
const LATemplatesView = lazy(() =>
    import("@accounting/la/templates/LATemplatesView").then((m) => ({ default: m.LATemplatesView })),
);
const PdfToolsApp = lazy(() => import("@pdf-tools/PdfToolsApp").then((m) => ({ default: m.PdfToolsApp })));
const RecordTextTemplatesView = lazy(() =>
    import("@accounting/common/items/recordTextTemplates/RecordTextTemplatesView").then((m) => ({
        default: m.RecordTextTemplatesView,
    })),
);
const PostingRulesView = lazy(() =>
    import("@accounting/common/items/PostingRulesView/PostingRulesView").then((m) => ({ default: m.PostingRulesView })),
);
const ViewerView = lazy(() => import("@app/pages/ViewerView").then((m) => ({ default: m.ViewerView })));
const AdminVatNumberView = lazy(() =>
    import("@app/pages/admin/AdminVatNumberView").then((m) => ({ default: m.AdminVatNumberView })),
);
const AdminMetroAccountingCategoriesView = lazy(() =>
    import("@app/pages/admin/AdminMetroAccountingCategoriesView").then((m) => ({
        default: m.AdminMetroAccountingCategoriesView,
    })),
);
const AdminMetroArticlesView = lazy(() =>
    import("@app/pages/admin/AdminMetroArticlesView").then((m) => ({ default: m.AdminMetroArticlesView })),
);
const DatevConvertDocumentsView = lazy(() =>
    import("@accounting/common/importExport/DatevConvertDocumentsView").then((m) => ({
        default: m.DatevConvertDocumentsView,
    })),
);

export const routeSwitch = (
    <Fragment>
        <Route path="/" element={<Navigate to={AppRoutes.home} />} />
        <Route
            index
            path={AppRoutes.home}
            element={
                <AuthWrapper.AuthenticatedRoute>
                    <HomeView />
                </AuthWrapper.AuthenticatedRoute>
            }
        />
        <Route
            path={AppRoutes.manageCompanies}
            element={
                <AuthWrapper.AuthenticatedRoute>
                    <CompaniesView />
                </AuthWrapper.AuthenticatedRoute>
            }
        />
        <Route
            path={AppRoutes.profile}
            element={
                // Reachable while waiting for a company: password, 2FA and language are
                // account settings that do not depend on a company being provisioned.
                <AuthWrapper.AuthenticatedRoute allowWithoutCompany>
                    <ProfileView />
                </AuthWrapper.AuthenticatedRoute>
            }
        />
        <Route
            path={AppRoutes.releaseNotes}
            element={
                <AuthWrapper.AuthenticatedRoute allowWithoutCompany>
                    <ReleaseNotesView />
                </AuthWrapper.AuthenticatedRoute>
            }
        />
        {/* ER*/}
        {/* ER=>Rechnungen */}
        <Route
            path={AppRoutes.invoice}
            element={<Navigate to={getRoute(AppRoutes.invoiceModule, { subGroup: ModuleSubGroups.ER })} />}
        />
        <Route
            path={getRoute(AppRoutes.invoiceModule, { subGroup: ModuleSubGroups.ER })}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Er}>
                    <ERView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={getRoute(AppRoutes.invoiceAnalysisOpos, { subGroup: ModuleSubGroups.ER })}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Er}>
                    <AuswertungOposViewER />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={getRoute(AppRoutes.invoiceAnalysisAvis, { subGroup: ModuleSubGroups.ER })}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Er}>
                    <AuswertungAvisViewER />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={getRoute(AppRoutes.invoiceAnalysisFalligkeit, { subGroup: ModuleSubGroups.ER })}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Er}>
                    <AuswertungFalligkeitER />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={getRoute(AppRoutes.invoiceFestschreibung, { subGroup: ModuleSubGroups.ER })}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Er}>
                    <FestschreibungERView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={getRoute(AppRoutes.invoiceTemplates, { subGroup: ModuleSubGroups.ER })}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Er}>
                    <ERTemplatesView />
                </AuthWrapper.ProductRoute>
            }
        />

        {/* ER=>Anzahlungen */}
        <Route
            path={getRoute(AppRoutes.invoiceModule, { subGroup: ModuleSubGroups.ErA })}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Er}>
                    <ERAnzView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={getRoute(AppRoutes.invoiceAnalysisOpos, { subGroup: ModuleSubGroups.ErA })}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Er}>
                    <AuswertungOposViewErA />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={getRoute(AppRoutes.invoiceAnalysisAvis, { subGroup: ModuleSubGroups.ErA })}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Er}>
                    <AuswertungAvisViewErA />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={getRoute(AppRoutes.invoiceAnalysisFalligkeit, { subGroup: ModuleSubGroups.ErA })}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Er}>
                    <AuswertungFalligkeitViewErA />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={getRoute(AppRoutes.invoiceFestschreibung, { subGroup: ModuleSubGroups.ErA })}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Er}>
                    <FestschreibungAZView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={getRoute(AppRoutes.invoiceTemplates, { subGroup: ModuleSubGroups.ErA })}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Er}>
                    <ERTemplatesView isAZ />
                </AuthWrapper.ProductRoute>
            }
        />

        {/* KB */}
        <Route
            path={AppRoutes.kassenbuch}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Kb}>
                    <KbBankHomePage productKey={GQL.IProductKey.Kb} />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.kassenbuchTemplates}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Kb}>
                    <KBTemplatesView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.kassenbuchOverview}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Kb}>
                    <KBView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.kassenbuchManage}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Kb}>
                    <KBManageView />
                </AuthWrapper.ProductRoute>
            }
        />

        {/* Bank */}
        <Route
            path={AppRoutes.bank}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Bank}>
                    <KbBankHomePage productKey={GQL.IProductKey.Bank} />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.bankTemplates}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Bank}>
                    <BankTemplatesView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.bankOverview}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Bank}>
                    <BankView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.manageBanks}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Bank}>
                    <BanksManageView />
                </AuthWrapper.ProductRoute>
            }
        />

        {/* FE */}
        <Route
            path={AppRoutes.freieErfassung}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Fe}>
                    <FEView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.freieErfassungTemplates}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Fe}>
                    <FETemplatesView />
                </AuthWrapper.ProductRoute>
            }
        />

        {/* POS */}
        <Route
            path={AppRoutes.pos}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Pos}>
                    <POSView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.posTemplates}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Pos}>
                    <POSTemplatesView />
                </AuthWrapper.ProductRoute>
            }
        />

        {/*  Deb  */}
        <Route
            path={AppRoutes.debitor}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Deb}>
                    <DebView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.debitorTemplates}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Deb}>
                    <DebTemplatesView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.debitorAnalysisOpos}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Deb}>
                    <AuswertungOposViewDeb />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.debitorAnalysisAvis}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Deb}>
                    <AuswertungAvisViewDeb />
                </AuthWrapper.ProductRoute>
            }
        />

        {/* LA */}
        <Route
            path={AppRoutes.paySlip}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.La}>
                    <LAView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.paySlipTemplates}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.La}>
                    <LATemplatesView />
                </AuthWrapper.ProductRoute>
            }
        />

        {/*  Common Overviews  */}
        <Route
            path={AppRoutes.calendar}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon}>
                    <CalendarPageView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.SuSa}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon}>
                    <SusaView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.BWA}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon}>
                    <BWAView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route path={AppRoutes.UStVA} element={<Navigate to={AppRoutes.UStVA + AppRoutes.UStVAOverview} />} />
        <Route
            path={AppRoutes.UStVA + "/*"}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon}>
                    <VatView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.accountsView}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon}>
                    <KontoAuskunftView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.filtersAndSearchView}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon}>
                    <CommonAuswertungView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route path={AppRoutes.manageAccount} element={<Navigate to={AppRoutes.manageAccountCategories} />} />
        <Route
            path={AppRoutes.manageAccountCategories}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon}>
                    <EditCategoryView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.manageAccountCreditors}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon}>
                    <EditCreditorView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.manageAccountDebitor}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon}>
                    <EditDebView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.manageAccountTag}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon}>
                    <EditTagView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.manageAccountRecordText1Templates}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon}>
                    <RecordTextTemplatesView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.manageAccountRecordText2Templates}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon}>
                    <RecordTextTemplatesView isText2 />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.manageAccountRules}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon}>
                    <PostingRulesView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.contacts}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Contacts}>
                    <ContactsListView />
                </AuthWrapper.ProductRoute>
            }
        />

        <Route path={AppRoutes.recordImportExport} element={<Navigate to={AppRoutes.recordImport} />} />
        <Route
            path={AppRoutes.recordImport}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon}>
                    <DatevImportRecordsView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.recordImportAccounts}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon}>
                    <DatevImportAccountsView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.datevConvertDocuments}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon}>
                    <DatevConvertDocumentsView />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.recordExport}
            element={
                <AuthWrapper.ProductRoute productKey={ProductKeys.AccountingCommon} role={GQL.IUserRole.DatevExporter}>
                    <DatevExportView />
                </AuthWrapper.ProductRoute>
            }
        />

        {/* Pdf Tool, we can allow partial company setup (without SKR and other accounting data) */}
        <Route
            path={AppRoutes.ocr + "/*"}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Ocr}>
                    <PdfToolsApp />
                </AuthWrapper.ProductRoute>
            }
        />
        <Route
            path={AppRoutes.dms + "/*"}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Dms}>
                    <DMS />
                </AuthWrapper.ProductRoute>
            }
        />

        {/*Banks*/}
        <Route
            path={AppRoutes.banks + "/*"}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Banks}>
                    <Banks />
                </AuthWrapper.ProductRoute>
            }
        />

        {/*Cash Registers*/}
        <Route
            path={AppRoutes.cashRegisters + "/*"}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.CashRegister}>
                    <CashRegisters />
                </AuthWrapper.ProductRoute>
            }
        />

        {/*AVM*/}
        <Route
            path={AppRoutes.avm + "/*"}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Avm}>
                    <Avm />
                </AuthWrapper.ProductRoute>
            }
        />

        {/*Invoices*/}
        <Route
            path={AppRoutes.invoicing + "/*"}
            element={
                <AuthWrapper.ProductRoute productKey={GQL.IProductKey.Invoices}>
                    <Invoices />
                </AuthWrapper.ProductRoute>
            }
        />

        <Route
            path={AppRoutes.adminVatNumbers}
            element={
                <AuthWrapper.AdminRoute>
                    <AdminVatNumberView />
                </AuthWrapper.AdminRoute>
            }
        />
        <Route
            path={AppRoutes.adminMetroAccountingCategories}
            element={
                <AuthWrapper.AdminRoute>
                    <AdminMetroAccountingCategoriesView />
                </AuthWrapper.AdminRoute>
            }
        />
        <Route
            path={AppRoutes.adminMetroArticles}
            element={
                <AuthWrapper.AdminRoute>
                    <AdminMetroArticlesView />
                </AuthWrapper.AdminRoute>
            }
        />

        <Route
            path={AppRoutes.authLogin}
            element={
                <AuthWrapper.GuestOnlyRoute>
                    <LoginView />
                </AuthWrapper.GuestOnlyRoute>
            }
        />
        <Route
            path={AppRoutes.authPasswordReset}
            element={
                <AuthWrapper.GuestOnlyRoute>
                    <PasswordResetView />
                </AuthWrapper.GuestOnlyRoute>
            }
        />
        <Route
            path={AppRoutes.authNewPassword}
            element={
                <AuthWrapper.GuestOnlyRoute>
                    <NewPasswordView />
                </AuthWrapper.GuestOnlyRoute>
            }
        />
        <Route
            path={AppRoutes.authRegister}
            element={
                <AuthWrapper.GuestOnlyRoute>
                    <RegisterView />
                </AuthWrapper.GuestOnlyRoute>
            }
        />
        <Route path={AppRoutes.authView} element={<AuthActionsView />} />
        <Route path={AppRoutes.authVerifyEmail} element={<VerifyEmailView />} />

        <Route path="/test" element={<TestView />} />

        <Route
            path={AppRoutes.viewer + "/:channelId"}
            element={
                <AuthWrapper.AuthenticatedRoute>
                    <ViewerView />
                </AuthWrapper.AuthenticatedRoute>
            }
        />

        <Route path={AppRoutes.datevCallback} element={<OAuthCallback />} />
        <Route path={AppRoutes.m365Callback} element={<M365OAuthCallback />} />
        <Route path={AppRoutes.agb} element={<AGBView />} />
        <Route path={AppRoutes.impressum} element={<ImpressumView />} />
        <Route path={AppRoutes.datenschutz} element={<DatenschutzView />} />
        <Route path={AppRoutes.haftungsausschluss} element={<HaftungsausschlussView />} />
        <Route path={AppRoutes.cookies} element={<CookiesView />} />
        <Route path={AppRoutes.avv} element={<AVVView />} />
        <Route path="*" element={<NotFoundView />} />
    </Fragment>
);
